import React, { useState } from 'react';
import { FileText, ArrowRight, Shield, Loader2, CheckCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const LeadMagnet = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post('/api/leads/guide', {
        email: email.trim(),
        name: name.trim(),
        source: 'credit_guide_download'
      });
    } catch {}
    setSent(true);
    setSending(false);
  };

  if (dismissed) return null;

  return (
    <section className="py-14 md:py-20 bg-[#f8faf9] border-y border-gray-200" data-testid="lead-magnet">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            {/* Left: Content */}
            <div className="md:col-span-3 p-8 md:p-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#1a5c3b]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1a5c3b]" />
                </div>
                <span className="text-xs font-semibold text-[#1a5c3b] uppercase tracking-wider">
                  {language === 'en' ? 'Free Guide' : 'Guide gratuit'}
                </span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 leading-tight">
                {language === 'en'
                  ? '5 Steps to Get Auto Financing After a Refusal'
                  : '5 étapes pour obtenir un financement auto après un refus'}
              </h3>
              <ul className="space-y-2 mb-1">
                {[
                  language === 'en' ? 'Understand why you were refused' : 'Comprendre pourquoi vous avez été refusé',
                  language === 'en' ? 'What lenders actually look for' : 'Ce que les prêteurs regardent vraiment',
                  language === 'en' ? 'Steps to improve your chances' : 'Les étapes pour améliorer vos chances',
                  language === 'en' ? 'Mistakes to avoid that cause more refusals' : 'Les erreurs à éviter qui causent plus de refus',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-[#1a5c3b] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <div className="md:col-span-2 bg-gray-50 p-8 md:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-200">
              {sent ? (
                <div className="text-center" data-testid="lead-magnet-success">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {language === 'en' ? 'Check your inbox!' : 'Vérifiez votre boîte courriel!'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {language === 'en' ? 'Your guide is on the way.' : 'Votre guide est en route.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    {language === 'en' ? 'Get it free by email' : 'Recevez-le gratuitement par courriel'}
                  </p>
                  <Input
                    placeholder={language === 'en' ? 'Your name (optional)' : 'Votre nom (optionnel)'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-10 text-sm"
                    data-testid="lead-magnet-name"
                  />
                  <Input
                    type="email"
                    placeholder={language === 'en' ? 'Your email' : 'Votre courriel'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-10 text-sm"
                    data-testid="lead-magnet-email"
                  />
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#1a5c3b] hover:bg-[#165432] text-white font-semibold py-4 text-sm"
                    data-testid="lead-magnet-submit"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'en' ? 'Sending...' : 'Envoi...'}</>
                    ) : (
                      <>{language === 'en' ? 'Download Free Guide' : 'Télécharger le guide gratuit'} <ArrowRight className="ml-2 w-4 h-4" /></>
                    )}
                  </Button>
                  <div className="flex items-center justify-center gap-1.5">
                    <Shield className="w-3 h-3 text-gray-400" />
                    <span className="text-[10px] text-gray-400">
                      {language === 'en' ? 'We respect your privacy' : 'Nous respectons votre vie privée'}
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
